import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getSeedReports, getAllAnalyses } from '../utils/api';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import AnimatedNumber from '../components/AnimatedNumber';
import { Upload, FileText, TrendingUp, Zap, ChevronRight, Clock, CheckCircle, Sparkles } from 'lucide-react';
import './DashboardPage.css';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="chart-tooltip">
        <p className="chart-tooltip__label">{label}</p>
        {payload.map(p => (
          <p key={p.name} style={{ color: p.color }}>{p.name}: {p.value}%</p>
        ))}
      </div>
    );
  }
  return null;
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getSeedReports(), getAllAnalyses().catch(() => ({ data: [] }))])
      .then(([seed, mine]) => {
        setReports(seed.data || []);
        setAnalyses(mine.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const allMatches = [...analyses, ...reports];
  const completed = allMatches.filter(m => m.status === 'complete').length;
  const processing = allMatches.filter(m => m.status === 'processing').length;

  // Prefer the user's own most recent real analysis; only fall back to the
  // static seed match if they have none yet.
  const myCompleted = analyses.filter(m => m.status === 'complete' && m.report)
    .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
  const featured = myCompleted[0] || reports[0];

  // There's no concept of "which side is my team" for an uploaded match
  // (home/away just reflects camera framing), so a win-rate KPI can't be
  // honestly computed — average AI confidence across completed analyses is
  // the real, derivable number shown instead.
  const confidenceScores = allMatches
    .filter(m => m.status === 'complete' && m.report?.analysisConfidence != null)
    .map(m => m.report.analysisConfidence);
  const avgConfidence = confidenceScores.length
    ? Math.round(confidenceScores.reduce((a, b) => a + b, 0) / confidenceScores.length)
    : null;

  // Real confidence-over-time trend from actual completed analyses, oldest
  // to newest — not a fabricated "form" curve.
  const performanceData = analyses
    .filter(m => m.status === 'complete' && m.report?.analysisConfidence != null)
    .sort((a, b) => new Date(a.uploadedAt) - new Date(b.uploadedAt))
    .slice(-8)
    .map(m => ({ week: m.homeTeam?.slice(0, 10) || '—', form: m.report.analysisConfidence }));

  const trendDelta = performanceData.length >= 2
    ? performanceData[performanceData.length - 1].form - performanceData[0].form
    : null;

  const firstName = user?.name?.split(' ')[0] || 'Coach';
  const subtitle = myCompleted.length > 0
    ? `${myCompleted.length} of your own analys${myCompleted.length === 1 ? 'is' : 'es'} completed${avgConfidence != null ? `, averaging ${avgConfidence}% confidence` : ''}.`
    : 'Upload your first match to start building real tactical data.';

  return (
    <div className="dashboard fade-in">
      <div className="dashboard__hero card">
        <div>
          <div className="dashboard__eyebrow"><Sparkles size={12} /> Tactical Intelligence</div>
          <h1 className="dashboard__title">
            Welcome back, <span className="gradient-text">{firstName}</span>
          </h1>
          <p className="dashboard__subtitle">{subtitle}</p>
        </div>
        <Link to="/upload" className="btn btn-primary dashboard__cta">
          <Upload size={16} />
          Analyse new match
        </Link>
      </div>

      <div className="dashboard__kpis stagger">
        {[
          { icon: FileText, label: 'Total Reports', value: allMatches.length, color: '#00d4ff', sub: `${completed} completed` },
          { icon: CheckCircle, label: 'Analyses Done', value: completed, color: '#10b981', sub: 'AI processed' },
          { icon: Clock, label: 'Processing', value: processing, color: '#f59e0b', sub: 'In queue' },
          { icon: TrendingUp, label: 'Avg. Confidence', value: avgConfidence, suffix: '%', color: '#7c3aed', sub: 'Across completed analyses' },
        ].map(kpi => (
          <div key={kpi.label} className="kpi-card card card-hover">
            <div className="kpi-card__top">
              <div className="kpi-card__label">{kpi.label}</div>
              <div className="kpi-card__icon" style={{ '--kpi-color': kpi.color }}>
                <kpi.icon size={16} />
              </div>
            </div>
            <div className="kpi-card__value stat-value">
              {kpi.value != null ? <AnimatedNumber value={kpi.value} suffix={kpi.suffix || ''} /> : '—'}
            </div>
            <div className="kpi-card__sub">{kpi.sub}</div>
          </div>
        ))}
      </div>

      <div className="dashboard__grid">
        <div className="card dashboard__chart-card">
          <div className="card__header">
            <div>
              <h3>Confidence Trend</h3>
              <p>Real AI confidence per completed analysis, most recent {performanceData.length}</p>
            </div>
            {trendDelta != null && (
              <span className={`badge ${trendDelta >= 0 ? 'badge-green' : 'badge-red'}`}>
                {trendDelta >= 0 ? '↑' : '↓'} {Math.abs(trendDelta).toFixed(1)} pts
              </span>
            )}
          </div>
          {performanceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={performanceData}>
                <defs>
                  <linearGradient id="perfGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00d4ff" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#00d4ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="week" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="form" name="Confidence" stroke="#00d4ff" strokeWidth={2.5} fill="url(#perfGrad)" dot={{ fill: '#00d4ff', r: 3, strokeWidth: 0 }} activeDot={{ r: 5 }} animationDuration={900} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="dashboard__chart-empty">Upload and analyse a match to see your confidence trend</div>
          )}
        </div>

        {featured && (
          <div className="card dashboard__featured">
            <div className="card__header">
              <h3>Latest AI Report</h3>
              <span className="badge badge-green">Complete</span>
            </div>
            <div className="featured-match">
              <div className="featured-match__teams">
                <div className="featured-match__team">
                  <div className="team-badge">{featured.homeTeam[0]}</div>
                  <span>{featured.homeTeam}</span>
                </div>
                <div className="featured-match__score">
                  <span>{featured.report?.score?.home ?? '—'}</span>
                  <span className="score-divider">–</span>
                  <span>{featured.report?.score?.away ?? '—'}</span>
                </div>
                <div className="featured-match__team featured-match__team--right">
                  <span>{featured.awayTeam}</span>
                  <div className="team-badge">{featured.awayTeam[0]}</div>
                </div>
              </div>
              <div className="featured-match__meta">
                <span className="badge badge-blue">{featured.competition}</span>
                <span className="featured-match__date">{featured.matchDate}</span>
              </div>
              {featured.report?.coachingRecommendations?.[0] && (
                <div className="featured-match__insight">
                  <Zap size={13} />
                  <div>
                    <div className="featured-match__insight-label">Top Coaching Tip</div>
                    {featured.report.coachingRecommendations[0].recommendation}
                  </div>
                </div>
              )}
              <Link to={`/reports/${featured.id}`} className="btn btn-secondary" style={{ width: '100%', marginTop: 4 }}>
                View Full Report <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        )}

        <div className="card dashboard__recent">
          <div className="card__header">
            <h3>Recent Matches</h3>
            <Link to="/reports" className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }}>
              View all <ChevronRight size={13} />
            </Link>
          </div>
          <div className="recent-list">
            {loading ? (
              [1, 2, 3].map(i => <div key={i} className="recent-item recent-item--skeleton" />)
            ) : allMatches.slice(0, 5).map(m => (
              <Link key={m.id} to={`/reports/${m.id}`} className="recent-item">
                <div className="recent-item__top">
                  <div className="recent-item__teams">{m.homeTeam} vs {m.awayTeam}</div>
                  <span className={`badge badge-${m.status === 'complete' ? 'green' : m.status === 'processing' ? 'amber' : 'red'}`}>
                    {m.status}
                  </span>
                </div>
                {m.report?.analysisConfidence != null && (
                  <div className="progress-bar" style={{ margin: '8px 0 4px' }}>
                    <div className="progress-fill" style={{ width: `${m.report.analysisConfidence}%` }} />
                  </div>
                )}
                <div className="recent-item__meta">
                  <span>{m.report?.analysisConfidence != null ? `${m.report.analysisConfidence}% confidence` : ''}</span>
                  <span>{m.matchDate}</span>
                </div>
              </Link>
            ))}
            {!loading && allMatches.length === 0 && (
              <div className="empty-state">
                <FileText size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} />
                <p>No matches yet</p>
                <Link to="/upload" className="btn btn-primary" style={{ marginTop: 12, fontSize: 13 }}>Upload your first match</Link>
              </div>
            )}
          </div>
        </div>

        {featured?.report?.coachingRecommendations && (
          <div className="card dashboard__recommendations">
            <div className="card__header">
              <h3>AI Coaching Insights</h3>
              <span className="badge badge-purple">AI</span>
            </div>
            <div className="rec-list">
              {featured.report.coachingRecommendations.map((rec, i) => (
                <div key={i} className="rec-item">
                  <div className="rec-item__priority" data-priority={rec.priority}>
                    {rec.priority}
                  </div>
                  <div className="rec-item__body">
                    <div className="rec-item__category">{rec.category}</div>
                    <div className="rec-item__text">{rec.recommendation}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
